/**
 * Product media gallery — Swiper carousels.
 *
 * Loaded only when Theme Settings > Product gallery > "Enable Swiper carousel
 * gallery" is on. Powers two independent Swiper instances per gallery:
 *   - .pg-main    the main product image/video slider
 *   - .pg-thumbs  the thumbnail carousel (shows N at a time; horizontal row
 *                 below the image on mobile when "Show thumbnails on mobile" is on)
 * linked together through Swiper's `thumbs` module.
 *
 * This file intentionally does not touch the theme's own bundled JS. The
 * markup it targets uses custom class names (pg-main__*, pg-thumbs__*) so it
 * never collides with the theme's built-in gallery behaviour. It keeps
 * `.media-gallery__link` / `.media-gallery__thumbnail` / `data-active` on the
 * relevant elements so the theme's PhotoSwipe zoom keeps working and stays
 * in sync with these carousels.
 */
(function () {
	'use strict';

	function initGallery(root) {
		if (typeof window.Swiper === 'undefined') {
			return;
		}

		var mainEl = root.querySelector('.js-pg-main');
		var thumbsEl = root.querySelector('.js-pg-thumbs');

		if (!mainEl) {
			return;
		}

		var thumbsPerView = Number(root.dataset.thumbsPerView) || 5;
		var initialIndex = Number(mainEl.dataset.initialIndex) || 0;
		var thumbsPosition = root.dataset.thumbsPosition || 'left';
		var thumbsDirection = thumbsPosition === 'bottom' ? 'horizontal' : 'vertical';
		var mobileThumbsPerView = Number(root.dataset.mobileThumbsPerView) || 5;

		var thumbsSwiper = null;

		if (thumbsEl) {
			thumbsSwiper = new window.Swiper(thumbsEl, {
				wrapperClass: 'pg-thumbs__wrapper',
				slideClass: 'pg-thumbs__slide',
				// Mobile first: a horizontal row under the main image. From 1024px
				// (matches the CSS breakpoint) the desktop position takes over.
				direction: 'horizontal',
				slidesPerView: mobileThumbsPerView,
				spaceBetween: 8,
				breakpoints: {
					1024: {
						direction: thumbsDirection,
						slidesPerView: thumbsPerView,
						spaceBetween: 16
					}
				},
				watchSlidesProgress: true,
				freeMode: true,
				observer: true,
				observeParents: true,
				navigation: {
					nextEl: root.querySelector('.pg-thumbs__next'),
					prevEl: root.querySelector('.pg-thumbs__prev')
				}
			});
		}

		var mainSwiper = new window.Swiper(mainEl, {
			wrapperClass: 'pg-main__wrapper',
			slideClass: 'swiper-slide',
			initialSlide: initialIndex,
			speed: 400,
			observer: true,
			observeParents: true,
			thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
			navigation: {
				nextEl: mainEl.querySelector('.pg-main__next'),
				prevEl: mainEl.querySelector('.pg-main__prev')
			},
			pagination: {
				el: mainEl.querySelector('.pg-main__pagination'),
				clickable: true
			}
		});

		function setActiveThumbnail(activeIndex) {
			if (!thumbsEl) {
				return;
			}

			var thumbnails = thumbsEl.querySelectorAll('.media-gallery__thumbnail');
			thumbnails.forEach(function (thumbnail) {
				var isActive = Number(thumbnail.dataset.index) === activeIndex;
				thumbnail.classList.toggle('media-gallery__thumbnail--is-active', isActive);
			});
		}

		function updateVideoPlayback() {
			mainEl.querySelectorAll('.pg-main__slide--video video').forEach(function (video) {
				video.pause();
			});

			var activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
			var activeVideo = activeSlide && activeSlide.querySelector('video');

			if (activeVideo) {
				activeVideo.play().catch(function () {});
			}
		}

		mainSwiper.on('slideChange', function () {
			setActiveThumbnail(mainSwiper.activeIndex);
			updateVideoPlayback();
		});

		setActiveThumbnail(mainSwiper.activeIndex);
		updateVideoPlayback();

		// Clicking (or a programmatic .click(), e.g. from the theme's PhotoSwipe
		// zoom → thumbnail sync) a thumbnail navigates the main slider.
		if (thumbsEl) {
			thumbsEl.addEventListener('click', function (event) {
				var thumbnail = event.target.closest('.media-gallery__thumbnail');

				if (!thumbnail) {
					return;
				}

				event.preventDefault();
				mainSwiper.slideTo(Number(thumbnail.dataset.index) || 0);
			});
		}
	}

	function init() {
		document.querySelectorAll('.media-gallery--swiper-v2').forEach(initGallery);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
